/**
 * The application bar, rebuilt from `Builder planu - tydzien - programator.dc.html`'s first row.
 *
 * The mock's bar holds six things and this holds those six: the `Programy /` crumb, the plan's
 * name, the "Szablon" chip, a spacer, "Przypisz", and a 34×34 save. Everything else this
 * bar used to draw is gone from SIGHT and from sight only — see the stylesheet's header for the
 * list and for where each capability went. The two that need code rather than CSS:
 *
 * · **The plan's own acts are a ⋯ at the bar's right end** (beside the title until the owner moved
 *   it on 30 September 2026). They were the title's CONTEXT menu until
 *   17 September 2026 — right-click the name, or the keyboard menu key — and the owner's note was
 *   that the builder has no way to delete a plan or to end one. It had two of the four, behind a
 *   gesture nothing on screen suggested. The mock draws no mark beside the name and this is a
 *   deliberate departure from it: an act that cannot be found is not an act the product has, and
 *   two of these four are irreversible, which is not a thing to hide behind a right-click.
 *   The title goes back to the mock's plain `<span>`, since it is no longer a trigger.
 * · **Undo and redo are the chords alone.** `usePlanShortcuts` binds them at the document, so they
 *   work wherever focus is; the bar draws nothing for them because the mock draws nothing for them.
 *
 * **The `h1` still lives here.** `AppShell` moves focus to it after every route change, which is
 * what announces the screen, so this bar renders in every state including the failed ones.
 *
 * ── The save's refusal has no visible channel any more, and that is a deliberate loss ────────
 * This bar used to print "Pusta faza «Fundament»" as ordinary text beside the button, because on
 * `/plans/nowy` a trainer meets a grey Zapisz, presses it, and nothing happens. The mock draws no
 * such line and the owner's instruction of 8 September 2026 is that nothing outside the mock is
 * visible. The sentence now reaches a sighted trainer through `title` alone, and a screen-reader
 * user through `aria-describedby` and the save region. **Flagged for the owner rather than
 * smuggled back in:** a pointer user who never hovers still gets no explanation.
 */

import { Link } from '@tanstack/react-router';
import { Menu } from '@base-ui/react/menu';
import { useId, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import menuStyles from '~/shared/ui/MenuSurface.module.css';
import { MoreGlyph } from '~/shared/ui/MoreGlyph';
import { SaveGlyph } from '~/shared/ui/SaveGlyph';
import saveButton from '~/shared/ui/saveButton.module.css';
import visuallyHidden from '~/shared/ui/visuallyHidden.module.css';
import { PearlMark } from '~/shared/ui/PearlMark';
import { ViewSwitch } from '~/shared/ui/ViewSwitch';

import type { SaveBlock } from '../lib/useSavePlanDocument';
import type { PlanMetaView } from '../model/planMetaView';
import type { PlanLifecycleAct, PlanLifecycleEntry } from '../model/planLifecycleMenu';

import { PlanMetaPopover, type PlanMetaActions } from './PlanMetaPopover';
import styles from './BuilderTopBar.module.css';

interface BuilderTopBarProps {
  readonly planName: string;
  /** The plan's provenance — "Szablon" or "Plan klienta". */
  readonly chips: readonly string[];
  /**
   * "Własnym tempem" — the plan's scheduling MODE, or `null` on a calendar plan, which the week's own
   * mock leaves unmarked. A label, never a switch: nothing on this screen writes the mode (R1 settles
   * it at creation). See `BuilderChromeView.modeChip` for the open item this closes.
   */
  readonly modeChip: string | null;
  /** "Plan przypisany · tyg. 1–4 zrealizowane". Announced, never drawn — the mock has no chip. */
  readonly lock: string | null;
  /**
   * The save affordance, or `null` in every state where there is no document to save — loading,
   * error, refused, empty. Null rather than a refused button: those states have no plan behind
   * them, so a save with a reason would be explaining the wrong thing.
   */
  readonly save: BuilderSaveProps | null;
  readonly assign: BuilderAssignProps;
  /**
   * Whether the bar offers its two acts at all. `false` on `/plans/nowy` with no plan in the tab —
   * the recovery question or the "gone" notice — where there is nothing to save or assign, and a
   * refused ember square beside „Przywróć" would compete with the one act that screen has.
   */
  readonly showsActs: boolean;
  /**
   * Attached to the Assign button, so the dialog can hand focus back to it on close.
   *
   * A SIBLING prop rather than a field of `assign`: a ref reached through an object is a ref
   * accessed during render as far as `react-hooks/refs` is concerned, and it taints every other
   * field read off that object.
   */
  readonly assignButtonRef: RefObject<HTMLButtonElement | null>;
  /** The plan's own three fields, or `null` in every state where there is no document to edit. */
  readonly meta: BuilderMetaProps | null;
  /**
   * The irreversible acts the ⋯ may offer — deleting the plan, or ending the client's. Decided by
   * `model/planLifecycleMenu`, which is also where the reason an empty list is normal is written.
   */
  readonly lifecycle: readonly PlanLifecycleEntry[];
  readonly onLifecycleAct: (act: PlanLifecycleAct) => void;
  /**
   * The plan's other two views — Przegląd and Kalendarz — on a client's running plan, or `null`
   * where the plan has none (a library entry, or a fork whose assignment has not been read).
   */
  readonly views: BuilderViewsProps | null;
  /**
   * „Asystent" (ai-plan-builder, board S): opens the Buddy panel beside the week. `null` where Buddy
   * is not offered on this plan: outside the beta, or a plan assigned to a client (R2, R3).
   */
  readonly assistant: BuilderAssistantProps | null;
  /** „Asystent" itself, for the panel's close to hand focus back to. */
  readonly assistantButtonRef: RefObject<HTMLButtonElement | null>;
}

export interface BuilderViewsProps {
  readonly onOpenOverview: () => void;
  readonly onOpenCalendar: () => void;
}

export interface BuilderMetaProps {
  readonly view: PlanMetaView;
  readonly actions: PlanMetaActions;
  readonly onRename: (name: string) => void;
}

export interface BuilderAssignProps {
  /**
   * Whether the bar draws Przypisz at all. `false` on a client's running plan, which is already
   * assigned and can never be assigned from here (owner, 30 September 2026: a control that is never
   * usable on a screen is absent from it, not refused). A refusal that can clear — unsaved changes,
   * a read in flight, an empty plan — keeps the button and its reason.
   */
  readonly offered: boolean;
  readonly canAssign: boolean;
  /** Why not, already worded. `null` exactly when `canAssign` is true. */
  readonly disabledReason: string | null;
  readonly onOpen: () => void;
}

export interface BuilderSaveProps {
  readonly dirty: boolean;
  readonly saving: boolean;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
  /** "Zapisano", or the note that a save cannot be undone. Announced, never drawn. */
  readonly notice: string | null;
  /** Why the tree cannot be sent at all — a phase carrying no exercise. Already worded. */
  readonly blocked: SaveBlock | null;
  /**
   * Whether the bar offers Zapisz at all. `false` while the plan is an unsaved one built for a client
   * — the one sanctioned change to this bar (owner, 23 September 2026): the assignment is that
   * plan's save, and the button is absent rather than refused. The status line stays.
   */
  readonly offered: boolean;
  readonly onSave: () => void;
}

/** The sequence mock's 14×14 loop, at its own `stroke-width: 1.6`. Decorative: the chip's own word
 * is beside it and says the same thing. */
function LoopGlyph() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={styles.chipGlyph}
      aria-hidden="true"
    >
      <path d="M5.4 6.6h8.2a2.4 2.4 0 0 1 2.4 2.4v2M14.6 13.4H6.4A2.4 2.4 0 0 1 4 11V9" />
      <path d="m12.4 4.4 2.4 2.2-2.4 2.2M7.6 15.6l-2.4-2.2 2.4-2.2" />
    </svg>
  );
}

/** The mock's 14×14 person, at its own `stroke-width: 1.7`. */
function PersonGlyph() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={styles.assignGlyph}
      aria-hidden="true"
    >
      <circle cx="10" cy="6.6" r="3.1" />
      <path d="M4.2 16.4c0-3.2 2.6-5 5.8-5s5.8 1.8 5.8 5" />
    </svg>
  );
}

/**
 * The plan's name as the page's heading, or the field that renames it.
 *
 * The rename is opened from the ⋯ at the bar's right end (`PlanMenu`), so the draft lives in the bar
 * and both read it. The two consequences the earlier dashed-underline version already worked out
 * apply unchanged: focus goes BACK to the ⋯ when the field closes, and a commit that changes nothing
 * is not sent — or opening the field would mark the plan unsaved.
 */
function PlanHeading({
  planName,
  meta,
  draftName,
  onDraftChange,
  onClose,
}: {
  readonly planName: string;
  readonly meta: BuilderMetaProps | null;
  readonly draftName: string | null;
  readonly onDraftChange: (name: string) => void;
  readonly onClose: () => void;
}) {
  if (meta === null || draftName === null) {
    return (
      <h1 className={styles.planName} title={planName}>
        {planName}
      </h1>
    );
  }

  const commit = () => {
    const next = draftName.trim();
    onClose();
    // A blank is not a name the wire accepts (`[MinLength(1)]`), and an unchanged one is nothing to
    // send. Both close the field and write nothing.
    if (next.length === 0 || next === meta.view.name) return;
    meta.onRename(next);
  };

  return (
    <h1 className={styles.planName}>
      <input
        className={styles.planNameInput}
        value={draftName}
        aria-label={meta.view.nameLabel}
        // `PostPlanTemplateRequest.Name` is `[MaxLength(200)]`. Enforced at the input rather than
        // as a 400 after a save that also carried the whole tree.
        maxLength={meta.view.nameMaxLength}
        autoFocus
        onChange={(event) => {
          onDraftChange(event.target.value);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
          if (event.key === 'Escape') {
            // An editor absorbs the key that closes it — the screen listens for shortcuts.
            event.stopPropagation();
            onClose();
          }
        }}
      />
    </h1>
  );
}

/**
 * The plan's own acts — rename, type and cover, and the irreversible endings — behind a ⋯.
 *
 * It stood beside the title until 30 September 2026, when the owner moved it to the bar's right end
 * beside Przypisz and Zapisz, where the plan screen keeps its own ⋯.
 */
function PlanMenu({
  meta,
  lifecycle,
  onLifecycleAct,
  onRename,
  triggerRef,
}: {
  readonly meta: BuilderMetaProps;
  /** The irreversible acts this plan may be offered. Empty in every state that has none. */
  readonly lifecycle: readonly PlanLifecycleEntry[];
  readonly onLifecycleAct: (act: PlanLifecycleAct) => void;
  readonly onRename: () => void;
  readonly triggerRef: RefObject<HTMLButtonElement | null>;
}) {
  const { t } = useTranslation();
  const [metaOpen, setMetaOpen] = useState(false);

  return (
    <Menu.Root>
      <Menu.Trigger
        ref={triggerRef}
        className={styles.planMenu}
        aria-label={t('web:planCalendar.planActions.trigger')}
      >
        <MoreGlyph className={styles.planMenuGlyph} />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner className={menuStyles.positioner} sideOffset={6} align="end">
          <Menu.Popup className={menuStyles.popup}>
            <Menu.Item className={menuStyles.item} onClick={onRename}>
              <span className={menuStyles.itemLabel}>{meta.view.renameAction}</span>
            </Menu.Item>
            {/* The plan's type and its cover. The mock draws no thumbnail in the bar, so the panel
             * that used to hang off one is opened from here and positions against the ⋯. */}
            <Menu.Item
              className={menuStyles.item}
              onClick={() => {
                setMetaOpen(true);
              }}
            >
              <span className={menuStyles.itemLabel}>{meta.view.triggerLabel}</span>
            </Menu.Item>
            {/* The irreversible ones, below a rule and marked. An empty list draws neither — a
             * separator with nothing under it is a promise of an act that is not there. */}
            {lifecycle.length > 0 && <div className={menuStyles.separator} role="separator" />}
            {lifecycle.map((entry) => (
              <Menu.Item
                key={entry.act}
                className={`${String(menuStyles.item)} ${String(menuStyles.itemDestructive)}`}
                onClick={() => {
                  onLifecycleAct(entry.act);
                }}
              >
                <span className={menuStyles.itemLabel}>{entry.label}</span>
              </Menu.Item>
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>

      <PlanMetaPopover
        view={meta.view}
        actions={meta.actions}
        open={metaOpen}
        onOpenChange={setMetaOpen}
        /* Resolved at open time rather than held in a ref — see the prop. */
        anchor={() => triggerRef.current}
      />
    </Menu.Root>
  );
}

export interface BuilderAssistantProps {
  readonly open: boolean;
  /** Why Buddy cannot take a message now (switched off, the day's ceiling), or `null`. */
  readonly disabledReason: string | null;
  readonly onToggle: () => void;
}

export function BuilderTopBar({
  planName,
  chips,
  modeChip,
  lock,
  save,
  assign,
  showsActs,
  assignButtonRef,
  meta,
  lifecycle,
  onLifecycleAct,
  views,
  assistant,
  assistantButtonRef,
}: BuilderTopBarProps) {
  const { t } = useTranslation();
  const [draftName, setDraftName] = useState<string | null>(null);
  const menuTrigger = useRef<HTMLButtonElement>(null);
  const saveReason = useId();
  const saveShortcut = useId();
  const assignReason = useId();
  const assistantReason = useId();

  const disabled = save === null || save.saving || save.blocked !== null || !save.dirty;
  // An unsaved plan built for a client is not saved from here: the assignment is its save.
  const offersSave = save === null || save.offered;
  const reason =
    save === null
      ? t('web:planBuilder.saveDisabledReason')
      : save.saving
        ? t('web:planBuilder.saveInFlightReason')
        : (save.blocked?.reason ?? t('web:planBuilder.savePristineReason'));

  return (
    <header className={styles.bar}>
      <nav className={styles.crumb} aria-label={t('web:planBuilder.breadcrumb')}>
        <Link to="/plans" className={styles.crumbLink}>
          {t('web:planBuilder.breadcrumb')}
        </Link>{' '}
        <span className={styles.sep} aria-hidden="true">
          /
        </span>
      </nav>

      <PlanHeading
        planName={planName}
        meta={meta}
        draftName={draftName}
        onDraftChange={setDraftName}
        onClose={() => {
          setDraftName(null);
          // `requestAnimationFrame` rather than a bare `focus()`: the field is still in the tree at
          // the moment this runs — React has not re-rendered — and it is the ⋯ that opened it.
          requestAnimationFrame(() => {
            menuTrigger.current?.focus();
          });
        }}
      />

      {/* The MODE chip stands first, immediately behind the name, which is where the sequence mock
       * puts it — the provenance chip follows. Neutral, not accentwise: it states a fact about the
       * plan rather than offering an act, and the screen's accent marks are spoken for elsewhere. */}
      {modeChip != null && (
        <span className={styles.chip}>
          <LoopGlyph />
          {modeChip}
        </span>
      )}

      {chips.map((chip) => (
        <span key={chip} className={styles.chip}>
          {chip}
        </span>
      ))}

      <div className={styles.spacer} />

      {/* Always in the tree, empty when there is nothing to say. A region inserted TOGETHER with its
       * text is not reliably announced, so it is mounted from the first frame and only its content
       * changes. Drawn nowhere: the mock's bar carries no save state. */}
      <p className={styles.reason} role="status">
        {save?.notice ?? (save?.dirty === true ? t('web:planBuilder.unsaved') : '')}
      </p>

      {/* The lock sentence, announced and not drawn — see the stylesheet's header. */}
      {lock != null && <span className={styles.reason}>{lock}</span>}

      {views !== null && (
        <ViewSwitch
          items={[
            {
              key: 'overview',
              label: t('web:planCalendar.tabOverview'),
              current: false,
              onSelect: views.onOpenOverview,
            },
            {
              key: 'calendar',
              label: t('web:planCalendar.tabCalendar'),
              current: false,
              onSelect: views.onOpenCalendar,
            },
            {
              key: 'programming',
              label: t('web:planCalendar.tabProgramming'),
              current: true,
              onSelect: null,
            },
          ]}
        />
      )}

      {meta !== null && (
        <PlanMenu
          meta={meta}
          lifecycle={lifecycle}
          onLifecycleAct={onLifecycleAct}
          onRename={() => {
            // Opens on what was AUTHORED, not on what is displayed — starting from a fallback would
            // offer to save the placeholder as a real name on the first keystroke.
            setDraftName(meta.view.name);
          }}
          triggerRef={menuTrigger}
        />
      )}

      {assistant !== null && (
        <>
          <button
            ref={assistantButtonRef}
            type="button"
            className={`${String(styles.assign)} ${assistant.open ? String(styles.assistantOn) : ''}`}
            aria-pressed={assistant.open}
            aria-describedby={assistant.disabledReason === null ? undefined : assistantReason}
            onClick={assistant.onToggle}
          >
            <PearlMark className={styles.assistantMark} />
            {t('web:aiPlan.assistant.open')}
          </button>
          <span id={assistantReason} className={styles.reason}>
            {assistant.disabledReason ?? ''}
          </span>
        </>
      )}

      {showsActs && (
        <>
          {assign.offered && (
            <button
              type="button"
              ref={assignButtonRef}
              className={styles.assign}
              aria-disabled={assign.canAssign ? undefined : 'true'}
              aria-describedby={assign.canAssign ? undefined : assignReason}
              title={assign.disabledReason ?? undefined}
              onClick={() => {
                if (assign.canAssign) assign.onOpen();
              }}
            >
              <PersonGlyph />
              {/* The visible word is the verb alone — owner, 9 September 2026 — and the accessible name
               * still says what is assigned and to whom. The tail is INSIDE the button and after the
               * label, so the name CONTAINS the visible text: a control whose spoken name does not
               * start with the words on it is the 2.5.3 failure, and an `aria-label` here would replace
               * "Przypisz" rather than extend it. Same shape the day panel's two "+" pills use. */}
              {t('web:planBuilder.assign')}
              <span className={visuallyHidden.srOnly}>{t('web:planBuilder.assignA11yTail')}</span>
            </button>
          )}

          {offersSave && (
            <button
              type="button"
              className={saveButton.save}
              aria-label={
                save?.saving === true ? t('web:planBuilder.saving') : t('web:planBuilder.save')
              }
              aria-disabled={disabled ? 'true' : undefined}
              /* The chord ALWAYS, the refusal only when there is one. Cmd/Ctrl+S is the one way to
               * save from inside the day panel, which traps focus and `aria-hidden`s this bar. */
              aria-describedby={disabled ? `${saveReason} ${saveShortcut}` : saveShortcut}
              title={disabled ? reason : t('web:planBuilder.save')}
              onClick={disabled ? undefined : save?.onSave}
            >
              <SaveGlyph />
            </button>
          )}

          {assign.offered && (
            <span id={assignReason} className={styles.reason}>
              {assign.disabledReason ?? ''}
            </span>
          )}
          {offersSave && (
            <>
              <span id={saveReason} className={styles.reason}>
                {reason}
              </span>
              <span id={saveShortcut} className={styles.reason}>
                {t('web:planBuilder.saveShortcut')}
              </span>
            </>
          )}
        </>
      )}
    </header>
  );
}
